export function cardCartItems(cart, variantId) {
  return (cart?.items || []).filter(item => String(item.variant_id) === String(variantId) && !item.selling_plan_allocation && !item.parent_relationship && !item.item_components?.length && !Object.values(item.properties || {}).some(value => value !== '' && value != null));
}
export function cardCartQuantity(cart, variantId) {
  return cardCartItems(cart, variantId).reduce((quantity, item) => quantity + item.quantity, 0);
}
export function cardCartMutation(cart, variantId, direction, rules = {}) {
  const min = Math.max(1, Number(rules.min) || 1), step = Math.max(1, Number(rules.step) || 1), max = Number(rules.max) > 0 ? Number(rules.max) : Infinity;
  const items = cardCartItems(cart, variantId), quantity = cardCartQuantity(cart, variantId);
  if (![1, -1].includes(direction) || direction < 0 && !quantity || direction > 0 && quantity + (quantity ? step : min) > max) return null;
  if (!items.length) return {type: 'add', variantId: String(variantId), quantity: min};
  const item = items.at(-1);
  return {type: 'change', variantId: String(variantId), id: item.key, quantity: direction > 0 ? item.quantity + step : item.quantity <= min ? 0 : Math.max(min, item.quantity - step)};
}
const lineIdentity = item => JSON.stringify([String(item.variant_id), item.selling_plan_allocation?.selling_plan?.id || null, Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)), item.parent_relationship || null]);
export function createCardCartStore({readCart, mutate, onCart = () => {}, onAdded = () => {}}) {
  let cart = null, running = false, pendingRead = null, revision = 0;
  const jobs = [], listeners = new Set(), identities = new Map();
  const findLine = (value, identity) => value.items.find(item => lineIdentity(item) === identity);
  const planFor = (job, value) => {
    if (job.type === 'card') return cardCartMutation(value, job.variantId, job.direction, job.rules);
    if (job.type === 'form') return {type: 'form', formData: job.formData, variantId: job.variantId};
    const item = findLine(value, job.identity);
    if (!item) return null;
    return {type: 'change', id: item.key, variantId: String(item.variant_id), quantity: job.quantity};
  };
  const projected = () => {
    if (!cart) return null;
    const value = {...cart, items: cart.items.map(item => ({...item}))};
    for (const job of jobs) {
      const plan = planFor(job, value);
      if (!plan || plan.type === 'form') continue;
      if (plan.type === 'add') value.items.push({key: 'pending-' + plan.variantId, variant_id: plan.variantId, quantity: plan.quantity, properties: {}});
      else {
        const item = value.items.find(item => item.key === plan.id);
        if (item) item.quantity = plan.quantity;
        value.items = value.items.filter(item => item.quantity > 0);
      }
    }
    value.item_count = value.items.reduce((sum, item) => sum + item.quantity, 0);
    return value;
  };
  const notify = () => listeners.forEach(listener => listener(projected(), jobs.length > 0));
  const receive = next => {
    if (!Array.isArray(next?.items)) return;
    cart = next; revision++;
    for (const item of cart.items) identities.set(item.key, lineIdentity(item));
    notify();
  };
  const refresh = () => {
    if (pendingRead) return pendingRead;
    const version = revision;
    pendingRead = Promise.resolve().then(readCart).then(next => {
      if (!Array.isArray(next?.items)) throw new Error('Unable to update cart.');
      if (version === revision) receive(next);
      return cart;
    }).finally(() => {pendingRead = null;});
    return pendingRead;
  };
  const drain = async () => {
    if (running) return;
    running = true;
    const additions = [];
    let changed = false;
    while (jobs.length) {
      const job = jobs[0];
      const batch = [job];
      if (job.type === 'line') {
        for (const next of jobs.slice(1)) {
          if (next.type !== 'line' || next.identity !== job.identity) break;
          batch.push(next);
        }
      } else if (job.type === 'card' && cart && cardCartItems(cart, job.variantId).length <= 1) {
        for (const next of jobs.slice(1)) {
          if (next.type !== 'card' || next.variantId !== job.variantId || JSON.stringify(next.rules) !== JSON.stringify(job.rules)) break;
          batch.push(next);
        }
      }
      try {
        if (!cart) await refresh();
        let plan = planFor(batch.at(-1), cart);
        if (job.type === 'card' && batch.length > 1) {
          const working = {...cart, items: cart.items.map(item => ({...item}))};
          for (const entry of batch) {
            const stepPlan = planFor(entry, working);
            if (!stepPlan) continue;
            if (stepPlan.type === 'add') working.items.push({key: 'pending-' + stepPlan.variantId, variant_id: stepPlan.variantId, quantity: stepPlan.quantity, properties: {}});
            else {
              const item = working.items.find(item => item.key === stepPlan.id);
              if (item) item.quantity = stepPlan.quantity;
              working.items = working.items.filter(item => item.quantity > 0);
            }
          }
          const target = cardCartQuantity(working, job.variantId);
          const previous = cardCartItems(cart, job.variantId)[0];
          plan = target === cardCartQuantity(cart, job.variantId) ? null : previous ? {type: 'change', id: previous.key, variantId: String(job.variantId), quantity: target} : {type: 'add', variantId: String(job.variantId), quantity: target};
        }
        if (plan) {
          const previousLine = plan.type === 'change' ? cart.items.find(item => item.key === plan.id) : null;
          const result = await mutate(plan);
          jobs.splice(0, batch.length); receive(result.cart); changed = true;
          if (plan.type === 'add' || plan.type === 'form') additions.push({items: result.items, source: job.source});
          const actualLine = previousLine ? findLine(result.cart, lineIdentity(previousLine)) : null;
          const quantity = plan.type === 'change' ? actualLine?.quantity || 0 : plan.type === 'add' ? result.items?.[0]?.quantity : null;
          const adjusted = quantity != null && quantity !== plan.quantity;
          batch.forEach(entry => entry.resolve({changed: true, adjusted, quantity}));
        } else { jobs.splice(0, batch.length); notify(); batch.forEach(entry => entry.resolve(false)); }
      } catch (error) {
        jobs.splice(0, batch.length);
        try {await refresh(); changed = true;} catch {}
        notify(); batch.forEach(entry => entry.reject(error));
      }
    }
    running = false;
    if (changed && cart) onCart(cart, additions.length ? 'variant:add' : 'line-item:change');
    for (const addition of additions) onAdded(addition.items, cart, addition.source);
    notify();
  };
  const enqueue = job => new Promise((resolve, reject) => {
    jobs.push({...job, resolve, reject}); notify(); Promise.resolve().then(drain);
  });
  return {
    receive, refresh,
    subscribe(listener) {listeners.add(listener); listener(projected(), jobs.length > 0); return () => listeners.delete(listener);},
    change(variantId, direction, rules, source) {return enqueue({type: 'card', variantId, direction, rules, source});},
    setLine(key, quantity, source) {
      if (!Number.isInteger(quantity) || quantity < 0 || !identities.has(key)) return Promise.reject(new Error('Unable to update quantity.'));
      return enqueue({type: 'line', identity: identities.get(key), quantity, source});
    },
    addForm(formData, source) {return enqueue({type: 'form', formData, variantId: formData.get('id'), source});},
    lineQuantity(key) {const item = findLine(projected() || {items: []}, identities.get(key)); return item?.quantity || 0;},
    lineKey(key) {return findLine(cart || {items: []}, identities.get(key))?.key;},
    get busy() {return jobs.length > 0;}
  };
}

if (typeof document !== 'undefined') {
  const root = window.Shopify?.routes?.root || '/';
  const request = async (path, options = {}) => {
    const response = await fetch(root + path, {credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000), ...options});
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'Unable to update cart.');
    return data;
  };
  const sectionCart = sections => {
    for (const html of Object.values(sections || {})) {
      if (typeof html !== 'string') continue;
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-fauniva-cart-json]');
      if (node) {try {return JSON.parse(node.textContent);} catch {}}
    }
    return null;
  };
  const readCart = () => request('cart.js');
  const store = createCardCartStore({
    readCart,
    async mutate(plan) {
      const adding = plan.type === 'add' || plan.type === 'form';
      const sections = adding ? ['variant-added'] : [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {bubbles: true, detail: {sections}}));
      const sectionsUrl = adding ? root + 'variants/' + plan.variantId : root + 'cart';
      const sectionNames = [...new Set(sections)].slice(0, 5);
      let body, headers = {'X-Requested-With': 'XMLHttpRequest'};
      if (plan.type === 'form') {
        body = plan.formData; body.set('sections', sectionNames.join(',')); body.set('sections_url', sectionsUrl);
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({...adding ? {items: [{id: plan.variantId, quantity: plan.quantity}]} : {id: plan.id, quantity: plan.quantity}, sections: sectionNames, sections_url: sectionsUrl});
      }
      const result = await request('cart/' + (adding ? 'add' : 'change') + '.js', {method: 'POST', headers, body});
      const cart = adding ? sectionCart(result.sections) || await readCart() : result;
      cart.sections = result.sections;
      return {cart, items: result.items || [result]};
    },
    onCart(cart, baseEvent) {document.documentElement.dispatchEvent(new CustomEvent('cart:change', {bubbles: true, detail: {cart, baseEvent}}));},
    onAdded(items, cart, source) {
      if (window.themeVariables?.settings?.pageType === 'cart') return;
      if (window.themeVariables?.settings?.cartType === 'page' && window.themeVariables?.settings?.pageType !== 'cart') {window.location.assign(root + 'cart'); return;}
      const target = source?.isConnected ? source : document.documentElement;
      target.dispatchEvent(new CustomEvent('variant:add', {bubbles: true, detail: {items, cart}}));
    }
  });
  try {store.receive(JSON.parse(document.querySelector('[data-fauniva-initial-cart]')?.textContent || 'null'));} catch {}
  window.faunivaCart = store;
  document.addEventListener('cart:change', event => store.receive(event.detail?.cart));
  document.addEventListener('cart:refresh', () => {if (!store.busy) store.refresh().catch(() => {});});
  window.addEventListener('pageshow', event => {if (event.persisted) store.refresh().catch(() => {});});
  document.addEventListener('visibilitychange', () => {if (!document.hidden && !store.busy) store.refresh().catch(() => {});});
  document.addEventListener('cart:prepare-bundled-sections', event => {
    if (window.themeVariables?.settings?.pageType === 'cart') {
      const section = document.querySelector('.shopify-section--main-cart');
      if (section) event.detail.sections.push(section.id.replace('shopify-section-', ''));
    }
  });
  document.addEventListener('cart:change', async event => {
    if (window.themeVariables?.settings?.pageType !== 'cart') return;
    const current = document.querySelector('.shopify-section--main-cart');
    if (!current) return;
    const sectionId = current.id.replace('shopify-section-', '');
    let html = event.detail.cart.sections?.[sectionId];
    if (!html) {
      const response = await fetch(root + 'cart?section_id=' + encodeURIComponent(sectionId));
      if (!response.ok || store.busy) return;
      html = await response.text();
    }
    const updated = new DOMParser().parseFromString(html, 'text/html').getElementById(current.id);
    if (!updated) return;
    const focus = document.activeElement, key = focus?.dataset.lineKey, delta = focus?.dataset.faunivaLineDelta;
    const lineKey = key || focus?.closest('line-item-quantity')?.querySelector('[data-line-key]')?.dataset.lineKey;
    const currentKey = lineKey && store.lineKey(lineKey);
    const note = current.querySelector('[name="note"]')?.value;
    current.replaceChildren(...updated.childNodes);
    const noteInput = current.querySelector('[name="note"]'); if (noteInput && note != null) noteInput.value = note;
    if (currentKey) {
      const input = [...current.querySelectorAll('[data-line-key]')].find(input => input.dataset.lineKey === currentKey);
      (delta ? input?.closest('line-item-quantity')?.querySelector('[data-fauniva-line-delta="' + delta + '"]') : input)?.focus({preventScroll: true});
    }
  });
  store.subscribe((cart, busy) => {
    document.querySelectorAll('line-item-quantity [data-line-key]').forEach(input => {
      if (busy) input.value = store.lineQuantity(input.dataset.lineKey);
      input.closest('line-item-quantity').setAttribute('aria-busy', String(busy));
    });
    document.querySelectorAll('.fauniva-purchase-details [data-fauniva-candidate]').forEach(candidate => {
      candidate.hidden = (cart?.items || []).some(item => String(item.product_id) === candidate.dataset.productId || String(item.variant_id) === candidate.dataset.variantId);
    });
    document.querySelectorAll('.fauniva-purchase-details .fauniva-configured-upsells').forEach(container => {
      container.hidden = [...container.querySelectorAll('[data-fauniva-candidate]')].every(candidate => candidate.hidden);
    });
    document.querySelectorAll('[name="checkout"]').forEach(button => {
      if (busy && !button.disabled) {button.dataset.faunivaPending = 'true'; button.disabled = true;}
      else if (!busy && button.dataset.faunivaPending) {button.disabled = false; delete button.dataset.faunivaPending;}
    });
  });
  class FaunivaCardCart extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.unsubscribe?.(); this.abort = new AbortController();
      this.variantId = this.dataset.variantId; this.rules = {min: this.dataset.min, step: this.dataset.step, max: this.dataset.max};
      this.addButton = this.querySelector('[data-card-add]'); this.counter = this.querySelector('[data-card-counter]'); this.quantityLabel = this.querySelector('[data-card-quantity]'); this.errorLabel = this.querySelector('[data-card-error]'); this.quantity = Number(this.dataset.quantity) || 0;
      this.addEventListener('submit', event => {event.preventDefault(); this.change(1);}, {signal: this.abort.signal});
      this.addEventListener('click', event => {const button = event.target.closest('[data-card-delta]'); if (!button || !this.contains(button) || button.disabled) return; event.preventDefault(); this.change(Number(button.dataset.cardDelta));}, {signal: this.abort.signal});
      this.unsubscribe = store.subscribe((cart, busy) => this.update(cart, busy));
    }
    disconnectedCallback() {this.abort?.abort(); this.unsubscribe?.();}
    update(cart, busy) {
      const focused = document.activeElement, hadQuantity = this.quantity > 0;
      if (cart) this.quantity = cardCartQuantity(cart, this.variantId);
      const hasQuantity = this.quantity > 0;
      this.dataset.quantity = String(this.quantity); this.addButton.hidden = hasQuantity; this.counter.hidden = !hasQuantity; this.quantityLabel.textContent = String(this.quantity);
      this.setAttribute('aria-busy', String(busy)); this.addButton.disabled = false;
      this.querySelectorAll('[data-card-delta]').forEach(button => {const next = this.quantity + (this.quantity ? Number(this.rules.step) || 1 : Number(this.rules.min) || 1);button.disabled = Number(button.dataset.cardDelta) > 0 && Number(this.rules.max) > 0 && next > Number(this.rules.max);});
      if (this.contains(focused) && hadQuantity !== hasQuantity) (hasQuantity ? this.querySelector('[data-card-delta="1"]') : this.addButton)?.focus({preventScroll: true});
    }
    async change(direction) {
      this.errorLabel.hidden = true;
      try {
        const result = await store.change(this.variantId, direction, this.rules, this);
        if (result?.adjusted) throw new Error(this.dataset.adjustedMessage.replace('__COUNT__', result.quantity));
      } catch (error) {this.errorLabel.textContent = error.message || this.dataset.error; this.errorLabel.hidden = false;}
    }
  }
  if (!customElements.get('fauniva-card-cart')) customElements.define('fauniva-card-cart', FaunivaCardCart);
}
